/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Fetches current weather for the landing page card using PAPI workLocation + Open-Meteo.
 *
 * Resolution: PAPI workLocation.city → Open-Meteo geocoding → Open-Meteo weather API.
 * If PAPI fails or city can't be resolved, weather card is not shown.
 * Results are cached for 15 minutes in sessionStorage.
 */

import moment from 'moment-timezone';
import { logger } from 'src/logger';

export interface WeatherSnapshot {
  location: string;
  temperature: number; // Fahrenheit
  shortForecast: string;
  isDaytime: boolean;
  lastUpdated: string;
  weatherLink: string;
  dayHigh: number | null;
  dayLow: number | null;
}

export type TempUnit = 'F' | 'C';

const CACHE_KEY_PREFIX = 'finsuite-weather-v3';
const CACHE_TTL_MS = 15 * 60 * 1000;

const US_TIMEZONES = [
  'America/Los_Angeles',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Detroit',
  'America/Indianapolis',
  'Pacific/Honolulu',
  'America/Anchorage'
];

/** Returns true if user's browser timezone is in the US. */
export const isUSLocation = (): boolean => US_TIMEZONES.includes(moment.tz.guess());

/** Convert Fahrenheit to display temperature in the requested unit. */
export const toDisplayTemp = (f: number, unit: TempUnit): number => (unit === 'C' ? Math.round(((f - 32) * 5) / 9) : f);

// --- Cache ---

const getCached = (key: string): WeatherSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY_PREFIX}-${key}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: WeatherSnapshot; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const setCache = (key: string, data: WeatherSnapshot): void => {
  try {
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}-${key}`, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* ignore quota errors */
  }
};

// --- Open-Meteo ---

interface OpenMeteoResponse {
  current: { temperature_2m: number; weather_code: number; is_day: number; time: string };
  daily: { temperature_2m_max: number[]; temperature_2m_min: number[] };
}

const wmoCodeToForecast = (code: number): string => {
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly Cloudy';
  if (code <= 49) return 'Foggy';
  if (code <= 59) return 'Drizzle';
  if (code <= 69) return 'Rain';
  if (code <= 79) return 'Snow';
  if (code <= 84) return 'Rain Showers';
  if (code <= 94) return 'Snow Showers';
  return 'Thunderstorm';
};

const fetchOpenMeteo = async (
  office: { label: string; lat: number; lon: number; weatherLink: string },
  cacheKey: string
): Promise<WeatherSnapshot | null> => {
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${office.lat}&longitude=${office.lon}&current=temperature_2m,weather_code,is_day&daily=temperature_2m_max,temperature_2m_min&timezone=auto&temperature_unit=fahrenheit&forecast_days=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data: OpenMeteoResponse = await res.json();
    return {
      location: office.label,
      temperature: Math.round(data.current.temperature_2m),
      shortForecast: wmoCodeToForecast(data.current.weather_code),
      isDaytime: data.current.is_day === 1,
      lastUpdated: data.current.time,
      weatherLink: office.weatherLink,
      dayHigh: Math.round(data.daily.temperature_2m_max[0]),
      dayLow: Math.round(data.daily.temperature_2m_min[0])
    };
  } catch {
    return null;
  }
};

// --- PAPI city resolution ---

const fetchWeatherByCity = async (city: string, country: string): Promise<WeatherSnapshot | null> => {
  const cacheKey = `papi-${city}-${country}`;
  const cached = getCached(cacheKey);
  if (cached) {
    logger?.info('Weather resolution', { logType: 'weather_resolution', source: 'papi_cache', city, country, status: 'cache_hit' });
    return cached;
  }

  try {
    const geoUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=en&format=json`;
    const geoRes = await fetch(geoUrl);
    if (!geoRes.ok) {
      logger?.warn('Weather resolution', {
        logType: 'weather_resolution',
        source: 'papi_geocode',
        city,
        country,
        status: 'geocode_http_error',
        httpStatus: geoRes.status
      });
      return null;
    }
    const geoData = (await geoRes.json()) as { results?: Array<{ latitude: number; longitude: number; name: string }> };
    if (!geoData.results?.length) {
      logger?.warn('Weather resolution', { logType: 'weather_resolution', source: 'papi_geocode', city, country, status: 'geocode_no_results' });
      return null;
    }

    const { latitude, longitude, name } = geoData.results[0];
    const weatherLink = `https://weather.com/weather/today/l/${latitude.toFixed(2)},${longitude.toFixed(2)}`;
    const office = { label: name.toUpperCase(), lat: latitude, lon: longitude, weatherLink };

    const snapshot = await fetchOpenMeteo(office, cacheKey);
    if (snapshot) {
      setCache(cacheKey, snapshot);
      logger?.info('Weather resolution', {
        logType: 'weather_resolution',
        source: 'papi',
        city,
        country,
        resolvedName: name,
        lat: latitude,
        lon: longitude,
        status: 'success'
      });
      return snapshot;
    }
    logger?.warn('Weather resolution', {
      logType: 'weather_resolution',
      source: 'papi_weather',
      city,
      country,
      resolvedName: name,
      status: 'weather_api_failed'
    });
    return null;
  } catch (err) {
    logger?.error('Weather resolution', { logType: 'weather_resolution', source: 'papi', city, country, status: 'exception', error: String(err) });
    return null;
  }
};

// --- Public API ---

/** Module-level cache — PAPI workLocation never changes during a session. */
let papiLocationCache: { city: string; country: string } | null = null;
let papiLocationResolved = false;

/**
 * Fetches current weather via PAPI workLocation city + Open-Meteo.
 * Returns null if PAPI fails or city cannot be resolved — weather card is not shown.
 * PAPI is called only once per session; subsequent calls use cached location.
 */
export const fetchWeather = async (options?: { userAlias?: string }): Promise<WeatherSnapshot | null> => {
  if (!options?.userAlias) return null;

  if (!papiLocationResolved) {
    papiLocationResolved = true;
    try {
      const { getEmployeeByLogin } = await import('src/api/papi-service');
      const response = await getEmployeeByLogin(options.userAlias, 'job');
      const loc = response.employee?.workLocation;
      logger?.info('Weather resolution', {
        logType: 'weather_resolution',
        source: 'papi_lookup',
        userAlias: options.userAlias,
        city: loc?.city ?? null,
        country: loc?.country ?? null,
        buildingCode: loc?.buildingCode ?? null,
        hasCityAndCountry: !!(loc?.city && loc?.country)
      });
      if (loc?.city && loc?.country) {
        papiLocationCache = { city: loc.city, country: loc.country };
      }
    } catch (err) {
      logger?.error('Weather resolution', {
        logType: 'weather_resolution',
        source: 'papi_lookup',
        userAlias: options.userAlias,
        status: 'papi_exception',
        error: String(err)
      });
    }
  }

  return papiLocationCache ? await fetchWeatherByCity(papiLocationCache.city, papiLocationCache.country) : null;
};
